import React, { useEffect, useRef, useState, useMemo } from "react";
import { isAxiosError } from "axios";
import { StorageKeys } from "@/common/constants/storageKeys";
import { BlockingDialog } from "@/components/atoms/blockingDialog";
import { useIntl } from "react-intl";
import { Lockr } from "@/common/Lockr";
import { setHasCC4TeamsTrue } from "@/repository/queryFns";
import {
	DebugInfoKey,
	WindowReloadReason,
	useDebugStore,
} from "@/common/stores/persistingStores/debugStore";
import { NetworkError } from "@/common/authentication";

const PollingIntervalWhenBeingOnline = 30000; // 30 seconds
const PollingIntervalWhenBeingOffline = 10000; // 10 seconds
const ConsecutiveFailuresToGoOffline = 3;

const OfflineDetector: React.FC = () => {
	const intl = useIntl();

	const [isOnline, setIsOnline] = useState(navigator.onLine);
	const isOnlineRef = useRef(isOnline);
	const consecutiveFailures = useRef(0);

	const pushDebugInfoItem = useDebugStore((state) => state.pushDebugInfoItem);

	const updateOnlineStatus = (newStatus: boolean) => {
		if (newStatus === isOnlineRef.current) {
			return;
		}

		setIsOnline(newStatus);
		isOnlineRef.current = newStatus;
	};

	const pollingInterval = useMemo(() => {
		return isOnline
			? PollingIntervalWhenBeingOnline
			: PollingIntervalWhenBeingOffline;
	}, [isOnline]);

	const checkWebConnectionTimeout = useRef<NodeJS.Timeout | null>(null);
	const reloadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const checkWebConnection = async () => {
		const userId = Lockr.get(StorageKeys.UserId);
		if (!userId) {
			return;
		}

		try {
			await setHasCC4TeamsTrue();

			consecutiveFailures.current = 0;

			if (!isOnlineRef.current) {
				pushDebugInfoItem(
					DebugInfoKey.WindowReloadTriggerEvents,
					WindowReloadReason.OfflineDetector
				);

				reloadTimeoutRef.current = setTimeout(() => {
					window.location.reload();
				}, 2000);
			}

			updateOnlineStatus(true);
		} catch (error) {
			console.error("Connection check failed:", error);

			const isNetworkError =
				(isAxiosError(error) && !error.response) ||
				error instanceof NetworkError;
			if (!isNetworkError) {
				return;
			}

			consecutiveFailures.current += 1;
			if (consecutiveFailures.current >= ConsecutiveFailuresToGoOffline) {
				updateOnlineStatus(false);
			}
		}
	};

	const startPolling = () => {
		if (checkWebConnectionTimeout.current) {
			clearInterval(checkWebConnectionTimeout.current);
		}
		checkWebConnectionTimeout.current = setInterval(
			checkWebConnection,
			pollingInterval
		);
	};

	useEffect(() => {
		checkWebConnection();
		startPolling();

		return () => {
			if (checkWebConnectionTimeout.current) {
				clearInterval(checkWebConnectionTimeout.current);
			}
		};
	}, [isOnline]);

	useEffect(() => {
		const handleOffline = () => {
			updateOnlineStatus(false);
		};

		window.addEventListener("offline", handleOffline);

		// Reload timer is cleared only on unmount so the [isOnline] effect
		// re-running on the offline → online transition doesn't cancel the
		// recovery reload it just scheduled.
		return () => {
			window.removeEventListener("offline", handleOffline);
			if (reloadTimeoutRef.current) {
				clearTimeout(reloadTimeoutRef.current);
				reloadTimeoutRef.current = null;
			}
		};
	}, []);

	return (
		<BlockingDialog
			isOpen={!isOnline}
			text={intl.formatMessage({
				id: "ErrorMessage.Offline",
			})}
		/>
	);
};

export default OfflineDetector;
